import { Container, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { useSnackbar } from 'notistack';
import {
  getNotificationSettings,
  getUsers,
  updateNotificationSettings,
} from 'src/services/user.service';
import SectionLayout from '../layout/section-layout';

export default function NotificationView() {
  // hooks
  const { organization } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  // variables
  const orgId = organization.organization_id;

  // states
  const [users, setUsers] = useState([]);
  const [dailyReport, setDailyReport] = useState({
    admin: null,
    owner: null,
  });

  const [candidateCommunication, setCandidateCommunication] = useState({
    jobCreator: null,
    owner: null,
    contactPerson: null,
  });

  const [calenderRequest, setCalenderRequest] = useState({
    interviewScheduler: null,
    jobCreator: null,
    owner: null,
    contactPerson: null,
  });

  // functions
  const fetchNotificationSettings = useCallback(async () => {
    if (!orgId) return;

    const res = await getNotificationSettings(orgId);

    if (res.success) {
      const response = res.body;

      const dailyReportSettings = response.daily_report_settings;

      setDailyReport({
        admin: {
          enabled: dailyReportSettings.admin_enabled,
        },
        owner: {
          enabled: dailyReportSettings.owner_enabled,
        },
      });

      const candidateCommunicationSettings =
        response.candidate_email_sender_settings;

      setCandidateCommunication({
        jobCreator: {
          enabled: candidateCommunicationSettings.job_creator_enabled,
        },
        owner: {
          enabled: candidateCommunicationSettings.owner_enabled,
        },
        contactPerson: {
          enabled: candidateCommunicationSettings.contact_person_enabled,
          id: candidateCommunicationSettings.contact_person_id,
        },
      });

      const calenderInviteSettings = response.calendar_invite_sender_settings;

      setCalenderRequest({
        interviewScheduler: {
          enabled: calenderInviteSettings.inperson_interview_creator_enabled,
        },
        jobCreator: {
          enabled: calenderInviteSettings.job_creator_enabled,
        },
        owner: {
          enabled: calenderInviteSettings.owner_enabled,
        },
        contactPerson: {
          enabled: calenderInviteSettings.contact_person_enabled,
          id: calenderInviteSettings.contact_person_id,
        },
      });
    }
  }, [orgId]);

  const getUsersList = useCallback(async () => {
    try {
      const response = await getUsers(orgId);
      if (response.success) {
        const _users = response.body?.client_users?.map((_user, index) => ({
          id: _user.user_id,
          name: _user.name,
          email: _user.email,
          role: _user.user_role,
          status: _user.client_status,
          isOwner: _user.client_owner,
          dateAdded: _user.date_added,
          inviteLink: _user.team_invite_link,
        }));

        setUsers(_users);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
    }
  }, [orgId]);

  const handleUpdateNotificationSettings = async (
    section,
    itemKey,
    itemSubKey,
    value,
  ) => {
    try {
      const clientKeysToServerKeys = {
        dailyReport: 'daily_report_settings',
        candidateCommunication: 'candidate_email_sender_settings',
        calenderRequest: 'calendar_invite_sender_settings',
        admin: 'admin',
        owner: 'owner',
        jobCreator: 'job_creator',
        contactPerson: 'contact_person',
        interviewScheduler: 'inperson_interview_creator',
        enabled: 'enabled',
        id: 'id',
      };

      const payload = {};

      switch (section) {
        case 'dailyReport':
          payload[clientKeysToServerKeys.dailyReport] = {
            [`${clientKeysToServerKeys.admin}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.owner}_${clientKeysToServerKeys.enabled}`]: false,
          };

          payload[clientKeysToServerKeys.dailyReport] = {
            ...payload[clientKeysToServerKeys.dailyReport],
            [`${clientKeysToServerKeys[itemKey]}_${clientKeysToServerKeys[itemSubKey]}`]:
              value,
          };

          setDailyReport(prevDailyReport => {
            const updatedDailyReport = { ...prevDailyReport };

            updatedDailyReport.admin.enabled = false;
            updatedDailyReport.owner.enabled = false;

            updatedDailyReport[itemKey][itemSubKey] = value;

            return updatedDailyReport;
          });
          break;
        case 'candidateCommunication':
          payload[clientKeysToServerKeys.candidateCommunication] = {
            [`${clientKeysToServerKeys.jobCreator}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.owner}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.id}`]:
              null,
          };

          if (itemSubKey === 'id') {
            payload[clientKeysToServerKeys.candidateCommunication] = {
              [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.enabled}`]: true,
            };
          }

          payload[clientKeysToServerKeys.candidateCommunication] = {
            ...payload[clientKeysToServerKeys.candidateCommunication],
            [`${clientKeysToServerKeys[itemKey]}_${clientKeysToServerKeys[itemSubKey]}`]:
              value,
          };

          setCandidateCommunication(prevCandidateCommunication => {
            const updatedCandidateCommunication = {
              ...prevCandidateCommunication,
            };

            updatedCandidateCommunication.jobCreator.enabled = false;
            updatedCandidateCommunication.owner.enabled = false;
            updatedCandidateCommunication.contactPerson.enabled = false;
            updatedCandidateCommunication.contactPerson.id = null;

            if (itemSubKey === 'id') {
              updatedCandidateCommunication.contactPerson.enabled = true;
            }

            updatedCandidateCommunication[itemKey][itemSubKey] = value;

            return updatedCandidateCommunication;
          });
          break;
        case 'calenderRequest':
          payload[clientKeysToServerKeys.calenderRequest] = {
            [`${clientKeysToServerKeys.interviewScheduler}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.jobCreator}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.owner}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.enabled}`]: false,
            [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.id}`]:
              null,
          };

          if (itemSubKey === 'id') {
            payload[clientKeysToServerKeys.calenderRequest] = {
              [`${clientKeysToServerKeys.contactPerson}_${clientKeysToServerKeys.enabled}`]: true,
            };
          }

          payload[clientKeysToServerKeys.calenderRequest] = {
            ...payload[clientKeysToServerKeys.calenderRequest],
            [`${clientKeysToServerKeys[itemKey]}_${clientKeysToServerKeys[itemSubKey]}`]:
              value,
          };

          setCalenderRequest(prevCalenderRequest => {
            const updatedCalenderRequest = { ...prevCalenderRequest };

            updatedCalenderRequest.interviewScheduler.enabled = false;
            updatedCalenderRequest.jobCreator.enabled = false;
            updatedCalenderRequest.owner.enabled = false;
            updatedCalenderRequest.contactPerson.enabled = false;
            updatedCalenderRequest.contactPerson.id = null;

            if (itemSubKey === 'id') {
              updatedCalenderRequest.contactPerson.enabled = true;
            }

            updatedCalenderRequest[itemKey][itemSubKey] = value;

            return updatedCalenderRequest;
          });
          break;
        default:
          break;
      }

      const response = await updateNotificationSettings(payload, orgId);

      if (response.success) {
        enqueueSnackbar('Notification settings updated successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to update notification settings', {
        variant: 'error',
      });
    } finally {
      fetchNotificationSettings();
    }
  };

  // effects
  useEffect(() => {
    getUsersList();
    fetchNotificationSettings();
  }, [fetchNotificationSettings, getUsersList]);

  return (
    <Container
      sx={{
        px: '60px',
        py: '40px',
      }}
    >
      <Stack>
        <Stack mb="40px">
          <Typography variant="h3">Notifications Settings</Typography>
        </Stack>
        <Stack spacing="20px">
          <SectionLayout
            heading="Daily Report"
            subHeading="Assign who will receive the report everyday"
            section="dailyReport"
            handleChange={handleUpdateNotificationSettings}
            {...dailyReport}
          />
          <SectionLayout
            heading="Candidate Communications"
            subHeading="Email sent from hireintel to the candidate reply - to email id"
            section="candidateCommunication"
            handleChange={handleUpdateNotificationSettings}
            users={users}
            {...candidateCommunication}
          />
          <SectionLayout
            heading="Calender Request"
            subHeading="Email sent from hireintel to the candidate reply - to email id"
            section="calenderRequest"
            handleChange={handleUpdateNotificationSettings}
            users={users}
            {...calenderRequest}
          />
        </Stack>
      </Stack>
    </Container>
  );
}
